import React, { Component } from 'react'
import { withFormValue } from '@bigfishtv/react-forms'
import { get, CancelToken, Cancel } from 'axios'
import AutosuggestInput from './AutosuggestInput'
import { GEOCODING_API_KEY } from '../constants/apiKeys'

import { addAll } from '@bigfishtv/cockpit/es/iconRegistry'
import Icon from '@bigfishtv/cockpit/es/components/Icon'

addAll({
  place: (
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
  ),
  close: (
    <path d="M23.2,2.1l-1.2-1.2L12,10.7L2.1,0.8L0.8,2.1l9.9,9.9l-9.9,9.9l1.2,1.2l9.9-9.9l9.9,9.9l1.2-1.2L13.3,12L23.2,2.1z" />
  ),
})

const AddressCell = ({ value, onRemove }) => (
  <div className="cell align-items-center border border-light background-white color-dark">
    <div className="cell-fixed">
      <Icon name="place" size={24} />
    </div>
    <div className="cell-flex">{(value || {}).address}</div>
    <div className="cell-fixed">
      <button type="button" className="button-s button-square button-primary button-border" onClick={() => onRemove()}>
        <Icon name="close" size={12} />
      </button>
    </div>
  </div>
)

class AddressInput extends Component {
  static defaultProps = {
    autoCache: true,
    allowPO: false,
    placeholder: 'Search for address...',
    componentRestrictions: null,
    requireStrictAddress: false,
  }

  constructor(props) {
    super(props)
    this.cache = {}
    this.inputValue = ''
    this.cancelLastRequest = null
    this.state = {
      suggestions: [],
      loading: false,
    }
  }

  getSuggestions(state) {
    const { value, reason } = state
    const { autoCache, componentRestrictions } = this.props
    if (reason != 'input-changed') return
    this.cancelOngoingRequest()
    if (autoCache && value in this.cache) {
      this.setState({ suggestions: this.cache[value] })
    } else {
      this.setState({ loading: true })
      const params = {
        key: GEOCODING_API_KEY,
        address: value,
      }
      if (componentRestrictions)
        params.components = Object.keys(componentRestrictions)
          .map((key) => `${key}:${componentRestrictions[key]}`)
          .join('|')
      get(`https://maps.googleapis.com/maps/api/geocode/json`, {
        params,
        cancelToken: new CancelToken((c) => (this.cancelLastRequest = c)),
      })
        .then(({ data }) => {
          const suggestions = data.results.map((result) => ({ title: result.formatted_address, result }))
          if (autoCache) this.cache[value] = suggestions
          this.setState({ loading: false, suggestions })
        })
        .catch((response) => !(response instanceof Cancel) && console.warn(response))
    }
  }

  cancelOngoingRequest() {
    if (this.cancelLastRequest) this.cancelLastRequest()
  }

  getAddressComponentValue(result, key, longName = false) {
    const component = result.address_components.find(({ types }) => ~types.indexOf(key))
    return component ? component[`${longName ? 'long' : 'short'}_name`] : null
  }

  handleSuggestionSelected(suggestion) {
    if (!suggestion) {
      this.props.onChange(null)
    } else {
      const { result } = suggestion
      if (this.props.requireStrictAddress) {
        const isStrictAddress = result.types.reduce(
          (found, type) =>
            found ? found : ['premise', 'subpremise', 'street_address', 'establishment', 'store'].indexOf(type) >= 0,
          false
        )
        if (!isStrictAddress) {
          alert('Address must be a postal address')
          return
        }
      }
      const newValue = {
        address: result.formatted_address,
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng,
        subpremise: this.getAddressComponentValue(result, 'subpremise'),
        street_number: this.getAddressComponentValue(result, 'street_number'),
        street_name: this.getAddressComponentValue(result, 'route'),
        suburb: this.getAddressComponentValue(result, 'locality'),
        city: this.getAddressComponentValue(result, 'administrative_area_level_2'),
        state: this.getAddressComponentValue(result, 'administrative_area_level_1'),
        country: this.getAddressComponentValue(result, 'country', true),
        postcode: this.getAddressComponentValue(result, 'postal_code'),
      }
      newValue.google_maps_url = `https://www.google.com/maps/place/${encodeURIComponent(newValue.address)}/@${
        newValue.lat
      },${newValue.lng},12z/`
      this.props.onChange(newValue)
    }
  }

  handleInputChange(value) {
    this.inputValue = value
  }

  handleInputBlur(event) {
    const { allowPO } = this.props
    if (!allowPO || !this.inputValue) return

    const postBoxMatches = [
      /CARE PO/,
      /CMA/,
      /CMB/,
      /CPA/,
      /GPO BOX/,
      /LOCKED BAG/,
      /MS/,
      /PO BOX/,
      /RSD/,
      /RMB/,
      /RMS/,
      /PRIVATE BAG/,
    ]
    const inputValueUpper = this.inputValue.toUpperCase()
    const isPostbox = postBoxMatches.reduce((found, test) => (found ? found : inputValueUpper.match(test)), false)
    if (isPostbox) {
      this.props.onChange({
        lat: null,
        lng: null,
        google_maps_url: null,
        address: this.inputValue,
      })
    }
  }

  render() {
    const { value, placeholder, formValue } = this.props
    const { suggestions, loading } = this.state
    return (
      <div>
        <AutosuggestInput
          value={value}
          onChange={(value) => this.handleSuggestionSelected(value)}
          placeholder={placeholder}
          suggestions={suggestions}
          loading={loading}
          getSuggestions={(state) => this.getSuggestions(state)}
          onInputBlur={(e) => this.handleInputBlur(e)}
          onInputChange={(value) => this.handleInputChange(value)}
          Cell={AddressCell}
        />
        {/* {formValue.completeErrorList.length > 0 && (
					<div className="form-message">
						<p className="form-message error">{formValue.completeErrorList[0].message}</p>
					</div>
				)} */}
      </div>
    )
  }
}

export default withFormValue(AddressInput)
